"use client";

// Phase 6 — Deploy Stream. Une grande fenêtre terminal sombre qui streame
// un faux déploiement CI/CD en live : git push → build → optimisations →
// upload edge → live. Les lignes apparaissent l'une après l'autre avec un
// timestamp, fade-in court, scroll automatique vers le bas. Une fois la
// dernière ligne écrite on tient un peu puis on reboucle.
//
// Cohérent avec la Phase 5 : même chrome (traffic lights + onglet),
// prolonge la narration "code → site → en ligne".

import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

type Line = {
  kind: "cmd" | "out" | "ok" | "info" | "user";
  text: string;
  // delay in seconds before this line appears
  at: number;
};

const LINES: Line[] = [
  { kind: "user", text: "$ git push origin main", at: 0.0 },
  { kind: "cmd",  text: "▸ Triggered build #847 on Vercel",                  at: 0.5 },
  { kind: "cmd",  text: "▸ Cloning kessem/your-site (depth=1)…",              at: 0.9 },
  { kind: "out",  text: "  847 packages · 0 vulnerabilities",                 at: 1.3 },
  { kind: "cmd",  text: "▸ npm ci",                                            at: 1.6 },
  { kind: "ok",   text: "✓ Dependencies installed (12.4s)",                    at: 2.3 },
  { kind: "cmd",  text: "▸ next build",                                        at: 2.6 },
  { kind: "out",  text: "  Compiling 24 pages · 18 components",                at: 3.0 },
  { kind: "out",  text: "  Optimizing images → AVIF / WebP (38 files)",        at: 3.5 },
  { kind: "out",  text: "  Generating static pages (24/24)",                   at: 4.0 },
  { kind: "ok",   text: "✓ Build complete (41.2s)",                            at: 4.5 },
  { kind: "cmd",  text: "▸ Uploading to edge network…",                        at: 4.9 },
  { kind: "ok",   text: "✓ Deployed to https://kessem.studio",                 at: 5.5 },
  { kind: "ok",   text: "✓ HTTPS certificate issued",                          at: 5.9 },
  { kind: "ok",   text: "✓ DNS propagated globally",                           at: 6.3 },
  { kind: "info", text: "ⓘ Lighthouse — Perf 98 · SEO 100 · A11y 100",         at: 6.8 },
  { kind: "ok",   text: "✓ Live in production · 53s total",                    at: 7.3 },
];

const LOOP = 10.5; // seconds before restart
const HOLD_AFTER = 7.6; // when the last meaningful line is written

function useLoopClock(period: number) {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = () => {
      const elapsed = ((performance.now() - start) / 1000) % period;
      setT(elapsed);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [period]);
  return t;
}

function fmt(ts: number) {
  // Pad to mm:ss.ms — gives the log a real CI feel.
  const total = ts;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  const ms = Math.floor((total - Math.floor(total)) * 100);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}.${ms
    .toString()
    .padStart(2, "0")}`;
}

const KIND_STYLES: Record<Line["kind"], string> = {
  user: "text-zinc-100",
  cmd:  "text-cyan-300",
  out:  "text-zinc-500",
  ok:   "text-emerald-300",
  info: "text-violet-300",
};

function TrafficLights() {
  return (
    <div className="flex items-center gap-1.5">
      <span className="size-2.5 rounded-full bg-rose-400/90" />
      <span className="size-2.5 rounded-full bg-amber-300/90" />
      <span className="size-2.5 rounded-full bg-emerald-400/90" />
    </div>
  );
}

export function DeployStream() {
  const elapsed = useLoopClock(LOOP);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const visible = LINES.filter((l) => elapsed >= l.at);
  const inProgress = elapsed >= 0.05 && elapsed < HOLD_AFTER + 0.5;

  // Auto-scroll to bottom as new lines appear.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [visible.length]);

  return (
    <motion.div
      aria-hidden
      initial={{ opacity: 1, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{
        y: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
        opacity: { duration: 0 },
      }}
      className="pointer-events-none fixed inset-0 hidden items-center justify-center md:flex"
    >
      <div className="w-full max-w-[860px] px-6">
        <div className="flex h-[420px] flex-col overflow-hidden rounded-xl border border-cyan-400/30 bg-[#0B1220] shadow-[0_30px_80px_-15px_rgba(34,211,238,0.45),0_0_0_1px_rgba(34,211,238,0.20)]">
          {/* Title bar */}
          <div className="flex items-center justify-between border-b border-white/5 bg-[#0F1A2E] px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 rounded-md bg-[#0B1220] px-2.5 py-1 font-mono text-[10px] text-zinc-100">
                <span className="size-1.5 rounded-sm bg-cyan-400/80" />
                <span>deploy.kessem — terminal</span>
                <span
                  className={`size-1 rounded-full ${
                    inProgress ? "animate-pulse bg-amber-300" : "bg-emerald-400"
                  }`}
                />
              </div>
              <div className="hidden font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500 sm:block">
                {inProgress ? "deploying…" : "live"}
              </div>
            </div>
            <TrafficLights />
          </div>

          {/* Header strip */}
          <div className="flex items-center justify-between border-b border-white/5 bg-[#0A1120] px-4 py-1.5 font-mono text-[9px] text-zinc-500">
            <span>
              <span className="text-zinc-400">kessem</span>
              <span className="mx-1.5 text-zinc-700">›</span>
              your-site
              <span className="mx-1.5 text-zinc-700">›</span>
              <span className="text-cyan-300">main</span>
            </span>
            <span className="hidden sm:inline">
              build <span className="text-cyan-300">#847</span> ·
              <span className="ms-1 text-emerald-300">{fmt(elapsed)}</span>
            </span>
          </div>

          {/* Stream */}
          <div
            ref={scrollRef}
            className="flex-1 space-y-[3px] overflow-hidden px-4 py-3 font-mono text-[12px] leading-[18px]"
          >
            {visible.map((line, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="flex gap-3 whitespace-pre"
              >
                <span className="select-none text-zinc-700">{fmt(line.at)}</span>
                <span className={KIND_STYLES[line.kind]}>{line.text}</span>
              </motion.div>
            ))}
            {/* Trailing cursor while deploy in progress */}
            {inProgress && visible.length < LINES.length && (
              <div className="flex gap-3">
                <span className="select-none text-transparent">{fmt(elapsed)}</span>
                <span
                  className="inline-block h-[14px] w-[7px] bg-cyan-300"
                  style={{ animation: "kessem-cursor-blink 1s steps(2) infinite" }}
                />
              </div>
            )}
          </div>

          {/* Footer status */}
          <div className="flex items-center justify-between border-t border-white/5 bg-[#0A1120] px-4 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em]">
            <span className="flex items-center gap-1.5 text-zinc-500">
              <span
                className={`size-1.5 rounded-full ${
                  inProgress ? "animate-pulse bg-amber-300" : "bg-emerald-400"
                }`}
              />
              {inProgress ? "deploying" : "production"}
            </span>
            <span className="text-zinc-500">
              region <span className="text-cyan-300">cdg1 · fra1 · iad1</span>
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
